function WineEducation() {
  return (
    <div className="bg-white rounded-lg shadow-md p-6 mt-8">
      <h2 className="text-2xl font-light text-gray-800 mb-6">What Makes a Good Vintage?</h2>

      <div className="space-y-6">
        {/* Introduction */}
        <p className="text-gray-600">
          A wine's vintage year tells you when the grapes were harvested. Weather conditions during
          the growing season dramatically affect grape quality and the resulting wine character.
        </p>

        {/* Key Factors */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-green-50 rounded-lg p-4">
            <h3 className="font-medium text-green-800 mb-2 flex items-center">
              <span className="mr-2">☀️</span> Ideal Conditions
            </h3>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Warm, sunny days for ripening</li>
              <li>• Cool nights to preserve acidity</li>
              <li>• Moderate rainfall in spring</li>
              <li>• Dry conditions at harvest</li>
              <li>• Long, gradual growing season</li>
            </ul>
          </div>

          <div className="bg-red-50 rounded-lg p-4">
            <h3 className="font-medium text-red-800 mb-2 flex items-center">
              <span className="mr-2">⚠️</span> Challenging Conditions
            </h3>
            <ul className="text-sm text-red-700 space-y-1">
              <li>• Spring frost during flowering</li>
              <li>• Hailstorms damaging vines</li>
              <li>• Excessive rain causing rot</li>
              <li>• Extreme heat or drought stress</li>
              <li>• Wildfires causing smoke taint</li>
            </ul>
          </div>
        </div>

        {/* The Growing Season */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-800 mb-3">The Growing Season Timeline</h3>
          <div className="grid grid-cols-4 gap-2 text-center text-sm">
            <div className="bg-white rounded p-2">
              <p className="font-medium text-purple-700">Spring</p>
              <p className="text-xs text-gray-600">Bud break & flowering</p>
            </div>
            <div className="bg-white rounded p-2">
              <p className="font-medium text-purple-700">Summer</p>
              <p className="text-xs text-gray-600">Fruit development</p>
            </div>
            <div className="bg-white rounded p-2">
              <p className="font-medium text-purple-700">Late Summer</p>
              <p className="text-xs text-gray-600">Véraison (ripening)</p>
            </div>
            <div className="bg-white rounded p-2">
              <p className="font-medium text-purple-700">Autumn</p>
              <p className="text-xs text-gray-600">Harvest</p>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="border-l-4 border-purple-500 pl-4">
          <h3 className="font-medium text-gray-800 mb-2">Keep in Mind</h3>
          <ul className="text-sm text-gray-600 space-y-2">
            <li>
              <strong>Regional variation:</strong> A challenging year in one region may be
              excellent in another. Always check specific regions.
            </li>
            <li>
              <strong>Producer skill matters:</strong> Top producers can make good wine even
              in difficult years through careful vineyard management and selection.
            </li>
            <li>
              <strong>Different grapes, different needs:</strong> Some grape varieties handle
              heat better, while others thrive in cooler conditions.
            </li>
            <li>
              <strong>Price and value:</strong> Challenging vintages often offer better value,
              while great vintages command premium prices.
            </li>
          </ul>
        </div>

        {/* Vintage Aging Guide */}
        <div className="bg-purple-50 rounded-lg p-4">
          <h3 className="font-medium text-purple-800 mb-3">Understanding Wine Aging</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-start">
              <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs font-medium mr-3 mt-0.5">1-3 yrs</span>
              <p className="text-purple-700">Young wines—fresh, fruity character. Most everyday wines are best here.</p>
            </div>
            <div className="flex items-start">
              <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs font-medium mr-3 mt-0.5">4-7 yrs</span>
              <p className="text-purple-700">Developing complexity. Quality wines start showing secondary flavors.</p>
            </div>
            <div className="flex items-start">
              <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs font-medium mr-3 mt-0.5">8-12 yrs</span>
              <p className="text-purple-700">Mature wines—tertiary notes emerge. Fine wines reach their peak.</p>
            </div>
            <div className="flex items-start">
              <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs font-medium mr-3 mt-0.5">12+ yrs</span>
              <p className="text-purple-700">Fully evolved. Only exceptional wines from great vintages improve further.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WineEducation
